'use client';

import type { Request } from '@cortex/database';
import {
  Button,
  Card,
  Center,
  Grid,
  Heading,
  Inline,
  MetadataField,
  Stack,
  Text,
} from '@cortex/ui';
import { Play } from 'lucide-react';

/**
 * Props for stage view components
 */
interface StageViewProps {
  request: Request;
}

/**
 * Props for CreateStageView component
 */
interface CreateStageViewProps extends StageViewProps {
  onStartInterview: () => void;
  isTransitioning: boolean;
}

/**
 * Create Stage View
 * Shows request details and action to start interview
 */
export default function CreateStageView({
  request,
  onStartInterview,
  isTransitioning,
}: CreateStageViewProps) {
  return (
    <Stack gap="lg">
      {/* Request Details Card */}
      <Card className="p-6">
        <Heading level={2} className="text-lg font-semibold">
          Request Details
        </Heading>

        <Stack gap="md" className="mt-4">
          <MetadataField label="Description">
            {request.description || (
              <Text as="span" color="muted" className="italic">
                No description provided
              </Text>
            )}
          </MetadataField>

          <Grid columns={2} gap="md">
            <MetadataField label="Type" valueClassName="mt-1 capitalize">
              {request.type}
            </MetadataField>
            <MetadataField label="Priority" valueClassName="mt-1 capitalize">
              {request.priority}
            </MetadataField>
          </Grid>
        </Stack>
      </Card>

      {/* Action Card */}
      <Card className="p-6">
        <Inline gap="md" align="center">
          <Center className="bg-primary/50 h-12 w-12 rounded-lg">
            <Play className="text-primary h-6 w-6" />
          </Center>
          <Stack gap="xs" className="flex-1">
            <Heading level={3} className="font-semibold">
              Ready to Start?
            </Heading>
            <Text color="muted" size="sm">
              Begin the interview to gather detailed requirements for your
              request.
            </Text>
          </Stack>
          <Button onClick={onStartInterview} disabled={isTransitioning}>
            {isTransitioning ? 'Starting...' : 'Start Interview'}
          </Button>
        </Inline>
      </Card>
    </Stack>
  );
}
